import QRCode from "react-qr-code";
import DownloadAppStore from "@/assets/images/download-appstore.png";
import DownloadGooglePlay from "@/assets/images/download-googleplay.png";
import { Typography } from "@/components/atoms";

interface LoginOptionProps {
  title: string;
  children: React.ReactNode;
  providerCode: string;
}

const LoginOption = ({ title, children, providerCode }: LoginOptionProps) => (
  <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-4">
    <Typography variant="action" className="mb-2.5 text-neutral-dark-gray">
      {title}
    </Typography>
    {children}
    <Typography variant="paragraph" className="text-neutral-dark-gray">
      Transport Provider Code: <span className="font-semibold uppercase text-neutral-dark-gray">{providerCode}</span>
    </Typography>
  </div>
);

export interface DownloadDriverAppStepProps {
  providerCode: string;
  driverRegisterLink?: string;
}

export const DownloadDriverAppStep = ({ providerCode, driverRegisterLink }: DownloadDriverAppStepProps) => (
  <div className="grid gap-3 lg:grid-cols-2">
    <div className="flex gap-3 rounded-lg border border-neutral-mid-gray bg-white p-6">
      <Typography variant="title" className="shrink-0 font-semibold text-neutral-dark-gray">
        1
      </Typography>
      <div className="flex flex-1 flex-col gap-3">
        <div>
          <Typography variant="action" className="text-neutral-dark-gray">
            Download the RideMinder Driver App
          </Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            This is your <span className="font-semibold">main work app</span>, used on your phone to run jobs day-to-day.
          </Typography>
        </div>

        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Download it from the App Store or Google Play on your mobile device.
        </Typography>

        <div className="flex flex-wrap gap-3">
          <a href={driverRegisterLink} target="_blank" rel="noopener noreferrer">
            <img src={DownloadAppStore} alt="Download on the App Store" className="w-[120px]" />
          </a>
          <a href={driverRegisterLink} target="_blank" rel="noopener noreferrer">
            <img src={DownloadGooglePlay} alt="Get it on Google Play" className="w-[135px]" />
          </a>
        </div>

        <Typography variant="paragraph" className="text-neutral-dark-gray">
          or Scan the QR to download
        </Typography>

        {driverRegisterLink && (
          <div className="w-fit">
            <QRCode value={driverRegisterLink} style={{ height: "125px", width: "125px" }} />
          </div>
        )}
      </div>
    </div>

    <div className="flex gap-3 rounded-lg border border-neutral-mid-gray bg-white p-6">
      <Typography variant="title" className="shrink-0 font-semibold text-neutral-dark-gray">
        2
      </Typography>
      <div className="flex flex-1 flex-col gap-3">
        <div>
          <Typography variant="action" className="text-neutral-dark-gray">
            Log in to the Driver App
          </Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            The Driver App uses a <span className="font-semibold">separate login</span> from this web portal.
          </Typography>
        </div>

        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Choose one option in the app:
        </Typography>

        <div className="flex flex-col gap-3">
          <LoginOption title="Option 1: Log in with an existing Driver account" providerCode={providerCode}>
            <Typography variant="paragraph" className="mb-4 text-neutral-dark-gray">
              Use this if you&apos;ve driven with RideMinder before and want to use that same account.
            </Typography>
          </LoginOption>

          <LoginOption title="Option 2: Create a New Driver Account" providerCode={providerCode}>
            <Typography variant="paragraph" className="mb-4 text-neutral-dark-gray">
              Create a new profile directly in the app.
            </Typography>
            <Typography variant="paragraph" className="mb-4 text-neutral-dark-gray">
              You&apos;ll be prompted to use a different email from your Solo Operator web account.
            </Typography>
          </LoginOption>
        </div>
      </div>
    </div>
  </div>
);
